import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Tooltip } from "@/components/atoms/Tooltip";
import { EmptyState } from "@/components/molecules/EmptyState";
import { ColumnCustomizer, type CustomizableColumnItem } from "@/components/molecules/ColumnCustomizer";
import { useTableColumnPreferences } from "@/hooks/useTableColumnPreferences";
import { clampTableColumnWidth } from "@/lib/tableColumnPreferences";
import type { SortDirection } from "@/types/api";

/**
 * Edge of the frozen first column, applied to its header and body cells alike.
 * Only worn once the table is scrolled horizontally, so the column sits flat
 * against its neighbour when everything fits. The shadow is deep enough to read
 * as a raised column with content passing beneath it, rather than as a border
 * with a faint smudge.
 */
const STICKY_COLUMN_EDGE =
  "shadow-[10px_0_18px_-6px_rgba(15,23,42,0.30)] dark:shadow-[1px_0_0_0_rgba(255,255,255,0.02),10px_0_18px_-6px_rgba(0,0,0,0.7)]";

export interface Column<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
  /**
   * Headers and values read from the left by default. Set `"right"` only for
   * columns that are not text — a trailing strip of icon buttons, say — since a
   * right-aligned label breaks the common left edge the eye scans down.
   * `"center"` suits a column whose values are badges or icons rather than text,
   * and holds even in a table whose other headers carry filters.
   */
  align?: "left" | "right" | "center";
  /** Extra classes applied to both the header cell and every body cell, so they stay aligned. */
  className?: string;
  /** Hover hint on the header label, e.g. what the column actually measures. */
  headerTooltip?: string;
  /**
   * Control rendered at the right-hand edge of the header cell — a
   * `ColumnFilter`, typically. Kept opposite the label so every header reads
   * "name on the left, control on the right" no matter how wide the column is.
   */
  headerAccessory?: ReactNode;
  /**
   * When set, the cell is single-line truncated and this text (newlines allowed)
   * is shown on hover — used for the first column so a long name/address ellipses
   * but the full value and its sub-details stay reachable.
   */
  cellTooltip?: (row: T) => string;
  /** Opt-in additional column that remains pinned during horizontal scrolling. */
  stickyClassName?: string;
}

/**
 * Opt-in multi-select. When supplied, the table grows a leading checkbox column;
 * selection state itself lives with the caller, so it can survive paging and
 * filtering rather than being reset every time the row array is rebuilt.
 */
export interface RowSelection<T> {
  /** Keys (per `rowKey`) currently selected, across all pages. */
  selectedKeys: Set<string>;
  onToggle: (row: T, next: boolean) => void;
  /** Select or clear every selectable row on the current page. */
  onToggleAll: (rows: T[], next: boolean) => void;
  /** Rows that cannot be selected — they render a disabled checkbox. */
  isSelectable?: (row: T) => boolean;
  /** Accessible label for the header checkbox. */
  selectAllLabel?: string;
}

export interface DataTableCustomization {
  /** Stable, application-wide identifier used in the localStorage key. */
  tableId: string;
  /** Columns that cannot be hidden. Header-filter columns are locked automatically. */
  lockedColumnIds?: string[];
  /** Header-filter columns that may still be hidden, opting out of the automatic lock. */
  optionalColumnIds?: string[];
  /** Required columns kept at the start so sticky positioning remains valid. */
  pinnedStartColumnIds?: string[];
  /** Action columns kept at the end of the table. */
  pinnedEndColumnIds?: string[];
  /** Columns available in the Columns panel but hidden in the original layout. */
  defaultHiddenColumnIds?: string[];
}

/** Id of the injected checkbox column. Prefixed so it cannot collide with a caller's column. */
const SELECTION_COLUMN_ID = "__selection__";

/**
 * Width of the checkbox column; the sticky offset below must match it. It must
 * exceed border (1px) + pl-8 (32px) + checkbox (16px) with slack to spare: at
 * an exact fit, sub-pixel rounding at some zoom levels lets the pinned column
 * beside it paint over the checkbox's right edge.
 */
const SELECTION_WIDTH = "w-14";
interface Props<T> {
  rows: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  rowKey: (row: T) => string;
  onRowClick?: (row: T,index: number) => void;
  selectedKey?: string | null;
  /**
   * Keeps the last clicked row tinted after the drawer it opened is dismissed,
   * so the user can see where they left off in a long list. Cleared as soon as
   * the list is refetched or the result set changes. Opt-in, because it only
   * means anything on tables whose rows open a detail drawer — elsewhere a row
   * that stays highlighted reads as a selection the user cannot dismiss.
   */
  markVisitedRow?: boolean;
  sort?: { field: string; direction: SortDirection };
  onSortChange?: (field: string, direction: SortDirection) => void;
  /** Omit for a plain table; supply to enable the checkbox column. */
  selection?: RowSelection<T>;
  /**
   * Tightens the vertical padding on every cell, header included, so a few more
   * rows fit in the same viewport. Opt-in: it only pays off on tables whose cells
   * are compact enough that the padding, rather than their content, sets the row
   * height.
   */
  dense?: boolean;
  /** Enables persisted visibility, ordering, and width controls for this table. */
  customization?: DataTableCustomization;
}

function widthFromClassName(width: string | undefined): number {
  if (!width) return 160;
  const px = width.match(/(?:^|\s)w-\[(\d+)px\]/);
  if (px) return Number(px[1]);
  const rem = width.match(/(?:^|\s)w-\[(\d+(?:\.\d+)?)rem\]/);
  if (rem) return Number(rem[1]) * 16;
  const scale = width.match(/(?:^|\s)w-(\d+)(?:\s|$)/);
  if (scale) return Number(scale[1]) * 4;
  return 160;
}

/** Checkbox that can also render the "some but not all" third state. */
function TriStateCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  // `indeterminate` is a DOM property with no HTML attribute, so it can only be
  // set imperatively — React will not do it from JSX.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className={cn(
        "h-4 w-4 cursor-pointer rounded border-slate-300 text-[#009BF5]",
        "focus:ring-2 focus:ring-[#009BF5]/40 focus:ring-offset-0",
        disabled && "cursor-not-allowed opacity-40",
      )}
    />
  );
}

export function DataTable<T>({
  rows,
  columns,
  loading,
  emptyTitle,
  emptyDescription,
  rowKey,
  onRowClick,
  selectedKey,
  markVisitedRow,
  sort,
  onSortChange,
  selection,
  dense,
  customization,
}: Props<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const columnPreferences = useTableColumnPreferences(columns, customization);
  const configuredColumns = columnPreferences.visibleColumns;
  const setColumnWidth = columnPreferences.setColumnWidth;
  const setDraftColumnWidth = columnPreferences.setDraftColumnWidth;
  const persistColumnWidths = columnPreferences.persistColumnWidths;
  const setDraftColumnWidthRef = useRef(setDraftColumnWidth);
  const persistColumnWidthsRef = useRef(persistColumnWidths);
  setDraftColumnWidthRef.current = setDraftColumnWidth;
  persistColumnWidthsRef.current = persistColumnWidths;
  // Whether the table is scrolled horizontally — drives the frozen first
  // column's divider/shadow so it only appears once content slides under it.
  const [scrolledX, setScrolledX] = useState(false);
  // Last row the user opened. Outlives the drawer it opened, unlike `selectedKey`.
  const [visitedKey, setVisitedKey] = useState<string | null>(null);

  /**
   * Index of the caller's first column. With selection on, index 0 is the injected
   * checkbox, so every "first column" rule below is expressed relative to this
   * rather than hard-coded to 0.
   */
  const firstDataIndex = selection ? 1 : 0;

  const selectableRows = useMemo(
    () => (selection?.isSelectable ? rows.filter(selection.isSelectable) : rows),
    [rows, selection],
  );
  const selectedOnPage = useMemo(
    () => (selection ? selectableRows.filter((r) => selection.selectedKeys.has(rowKey(r))).length : 0),
    [selectableRows, selection, rowKey],
  );
  const allOnPageSelected = selectedOnPage > 0 && selectedOnPage === selectableRows.length;

  const allColumns: Column<T>[] = useMemo(() => {
    if (!selection) return configuredColumns;
    const selectionColumn: Column<T> = {
      id: SELECTION_COLUMN_ID,
      header: "",
      width: SELECTION_WIDTH,
      cell: (row) => {
        const selectable = selection.isSelectable ? selection.isSelectable(row) : true;
        return (
          <TriStateCheckbox
            label="Select row"
            checked={selection.selectedKeys.has(rowKey(row))}
            disabled={!selectable}
            onChange={(next) => selection.onToggle(row, next)}
          />
        );
      },
    };
    return [selectionColumn, ...configuredColumns];
  }, [configuredColumns, selection, rowKey]);

  const resolvedColumnWidth = (column: Column<T>) => {
    if (column.id === SELECTION_COLUMN_ID) return 56;
    return columnPreferences.draftWidths[column.id] ??
      columnPreferences.widths[column.id] ??
      widthFromClassName(column.width);
  };

  const resizeState = useRef<{ id: string; startX: number; startWidth: number } | null>(null);
  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const resizing = resizeState.current;
      if (!resizing) return;
      setDraftColumnWidthRef.current(
        resizing.id,
        resizing.startWidth + event.clientX - resizing.startX,
      );
    };
    const onPointerUp = () => {
      if (resizeState.current) persistColumnWidthsRef.current();
      resizeState.current = null;
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      resizeState.current = null;
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };
  }, []);

  /**
   * Identity of the current result set. Derived from the row keys rather than
   * the array reference so callers that rebuild `rows` on every render (inline
   * `.map`/`.filter`) don't retrigger the scroll reset.
   */
  const rowsSignature = rows.length === 0
    ? "empty"
    : `${rows.length}:${rowKey(rows[0])}:${rowKey(rows[rows.length - 1])}`;

  useEffect(() => {
    // A new page (or new sort/filter result) should start at the top, not
    // wherever the previous page was scrolled to.
    for (let el = containerRef.current as HTMLElement | null; el; el = el.parentElement) {
      if (el.scrollTop > 0 && el.scrollHeight > el.clientHeight) el.scrollTop = 0;
    }
    // The visited marker is a breadcrumb within one set of results; once those
    // results are replaced it no longer points at anything meaningful.
    setVisitedKey(null);
  }, [rowsSignature]);

  /**
   * Also drop the marker the moment a refetch begins. `rowsSignature` alone
   * would miss a refresh that happens to return the same rows, and the request
   * is the point at which the user asked for fresh data.
   */
  const wasLoading = useRef(Boolean(loading));
  useEffect(() => {
    if (loading && !wasLoading.current) setVisitedKey(null);
    wasLoading.current = Boolean(loading);
  }, [loading]);

  const handleSort = (col: Column<T>) => {
    if (!col.sortable || !onSortChange) return;
    const next: SortDirection =
      sort?.field === col.id && sort.direction === "desc" ? "asc" : "desc";
    onSortChange(col.id, next);
  };

  /**
   * Both the checkbox column and the caller's first column stay pinned during
   * horizontal scroll, the second offset by the first's width. Only the rightmost
   * pinned column carries the divider/shadow, so the two read as one frozen block.
   */
  const isStickyColumn = (index: number) => {
    if (index === 0) return true;
    if (selection && index === 1) return true;
    return Boolean(allColumns[index]?.stickyClassName);
  };
  const stickyOffset = (index: number) => allColumns
    .slice(0, index)
    .reduce((offset, column, priorIndex) =>
      offset + (isStickyColumn(priorIndex) ? resolvedColumnWidth(column) : 0), 0);
  const lastStickyIndex = allColumns.reduce(
    (last, _column, index) => (isStickyColumn(index) ? index : last),
    firstDataIndex,
  );
  const isStickyEdge = (index: number) => index === lastStickyIndex;

  /**
   * Header filters need the label pinned to the column's left edge, so the
   * control can sit opposite it at the right and every header reads the same
   * way down the row. A table with no filters has nothing to make room for, so
   * it keeps the older look: first column left, last right, the rest centred.
   */
  const filtersInHeader = allColumns.some((c) => c.headerAccessory);
  const headerPad = dense ? "py-1.5" : "py-2.5";
  const cellPad = dense ? "py-2" : "py-3";

  const isCentered = (index: number) =>
    allColumns[index]?.align === "center" ||
    (!filtersInHeader && index > firstDataIndex && index !== allColumns.length - 1);

  const customizationItems = useMemo<CustomizableColumnItem[]>(() => {
    const movable = columnPreferences.orderedColumns.filter(
      (column) => !columnPreferences.fixedColumnIds.has(column.id),
    );
    return columnPreferences.orderedColumns.map((column) => {
      const movableIndex = movable.findIndex((item) => item.id === column.id);
      return {
        id: column.id,
        label: column.header || "Actions",
        visible: configuredColumns.some((item) => item.id === column.id),
        locked: columnPreferences.lockedColumnIds.has(column.id),
        fixed: columnPreferences.fixedColumnIds.has(column.id),
        canHide: columnPreferences.canHideColumn(column.id),
        canMoveUp: movableIndex > 0,
        canMoveDown: movableIndex >= 0 && movableIndex < movable.length - 1,
      };
    });
  }, [columnPreferences, configuredColumns]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {customization && (
        <div className="flex shrink-0 justify-end border-b border-bridge-border/60 bg-bridge-surface/70 px-3 py-2">
          <ColumnCustomizer
            columns={customizationItems}
            onToggle={columnPreferences.toggleColumn}
            onMove={columnPreferences.moveColumn}
            onReset={columnPreferences.reset}
          />
        </div>
      )}
      <div
        ref={containerRef}
        onScroll={(e) => setScrolledX(e.currentTarget.scrollLeft > 0)}
        className="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable] scrollbar-metal"
      >
      {/* min-w-max lets the declared column widths win when they don't fit the
          viewport — the table then scrolls horizontally under the frozen first
          column instead of squeezing every column narrower. */}
      <table className="w-full min-w-max table-fixed border-separate border-spacing-0 text-sm">
        {/* Column widths are pinned here, independent of any row. A sticky
            <thead> isn't reliably used by browsers to size fixed columns, so
            widths defined on <th> alone can be recomputed from body content and
            shift between pages — <colgroup> makes them immovable. */}
        <colgroup>
          {allColumns.map((c) => (
            <col key={c.id} className={c.width} style={{ width: resolvedColumnWidth(c) }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-20 bg-bridge-bg">
          <tr>
            {allColumns.map((c, index) => {
              const isSorted = sort?.field === c.id;
              const isSelectionColumn = c.id === SELECTION_COLUMN_ID;
              const pinned = isStickyColumn(index);
              const width = resolvedColumnWidth(c);
              const label = (
                <button
                  type="button"
                  onClick={() => handleSort(c)}
                  disabled={!c.sortable}
                  className={cn(
                    "inline-flex min-w-0 max-w-full items-center gap-1 uppercase text-[11px] tracking-wide",
                    c.sortable && "hover:text-bridge-fg cursor-pointer",
                    !c.sortable && "cursor-default",
                  )}
                >
                  <span>{c.header}</span>
                  {c.sortable && (isSorted ? (
                    sort!.direction === "asc"
                      ? <ArrowUp size={12} className="shrink-0 text-slate-600" />
                      : <ArrowDown size={12} className="shrink-0 text-slate-600" />
                  ) : <ArrowUpDown size={12} className="shrink-0 text-slate-400" />)}
                </button>
              );
              return (
                <th
                  key={c.id}
                  scope="col"
                  className={cn(
                    "relative font-semibold text-bridge-fg px-5 select-none border-y border-bridge-border",
                    headerPad,
                    // z-30 keeps the pinned corner above both the sticky header row
                    // and the sticky body column; the bg occludes cells scrolling under it.
                    pinned && "sticky z-30 bg-bridge-bg",
                    index === 0 && "border-l pl-8",
                    isSelectionColumn && "pl-8 pr-0",
                    isStickyEdge(index) && scrolledX && STICKY_COLUMN_EDGE,
                    index === allColumns.length - 1 && "border-r",
                    isCentered(index) ? "text-center" : c.align === "right" ? "text-right" : "text-left",
                    c.width,
                    c.className,
                  )}
                  style={{ width, left: pinned ? stickyOffset(index) : undefined }}
                >
                  {isSelectionColumn && selection ? (
                    <TriStateCheckbox
                      label={selection.selectAllLabel ?? "Select all rows on this page"}
                      checked={allOnPageSelected}
                      indeterminate={selectedOnPage > 0}
                      disabled={selectableRows.length === 0}
                      onChange={(next) => selection.onToggleAll(selectableRows, next)}
                    />
                  ) : (
                    /* Label hard against the column's left edge, filter control
                       hard against its right — the arrangement the eye can scan
                       down a row of headers without re-reading each one. */
                    <div
                      className={cn(
                        "flex w-full items-center gap-2",
                        isCentered(index)
                          ? "justify-center"
                          : c.align === "right" && !c.headerAccessory && "justify-end",
                      )}
                    >
                      {c.headerTooltip ? (
                        <Tooltip side="top" hoverOnly content={c.headerTooltip}>
                          {label}
                        </Tooltip>
                      ) : (
                        label
                      )}
                      {c.headerAccessory && <span className="ml-auto shrink-0">{c.headerAccessory}</span>}
                    </div>
                  )}
                  {customization && !isSelectionColumn && (
                    <div
                      role="separator"
                      aria-label={`Resize ${c.header || "Actions"} column`}
                      aria-orientation="vertical"
                      tabIndex={0}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        resizeState.current = { id: c.id, startX: event.clientX, startWidth: width };
                        document.body.style.cursor = "col-resize";
                        document.body.style.userSelect = "none";
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                        event.preventDefault();
                        event.stopPropagation();
                        const delta = event.key === "ArrowLeft" ? -8 : 8;
                        setColumnWidth(c.id, clampTableColumnWidth(width + delta, c.id === "actions" ? 64 : undefined));
                      }}
                      className="group absolute inset-y-0 right-0 z-40 w-2 cursor-col-resize touch-none focus-ring"
                    >
                      <span className="absolute inset-y-1 right-0 w-px bg-transparent transition-colors group-hover:bg-bridge-accent group-focus-visible:bg-bridge-accent group-active:bg-bridge-accent" />
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && Array.from({ length: 8 }).map((_, i) => (
            <tr key={`sk-${i}`}>
              {allColumns.map((c) => (
                <td key={c.id} className={cn("px-5 border-b border-bridge-border/40", cellPad)}>
                  <Skeleton className="h-4 w-3/5" />
                </td>
              ))}
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={allColumns.length}>
                <EmptyState title={emptyTitle ?? "No results"} description={emptyDescription} />
              </td>
            </tr>
          )}
          {rows.map((row,index) => {
            const k = rowKey(row);
            const selected = selectedKey === k || visitedKey === k;
            const checked = selection?.selectedKeys.has(k) ?? false;
            return (
              <tr
                key={k}
                style={{ animationDelay: `${Math.min(index, 12) * 25}ms` }}
                onClick={() => {
                  if (!onRowClick) return;
                  if (markVisitedRow) setVisitedKey(k);
                  onRowClick(row, index);
                }}
                className={cn(
                  // Rows carry an opaque background so the sticky first column
                  // (below) reads as the same colour and fully occludes cells
                  // scrolling beneath it — a translucent row would bleed through.
                  "group relative bg-white animate-rowIn motion-reduce:animate-none transition-[box-shadow,background-color,transform] duration-150",
                  onRowClick && "cursor-pointer hover:z-10 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.22)] dark:hover:shadow-[0_8px_20px_-8px_rgba(0,0,0,0.5),inset_0_1px_0_0_rgba(255,255,255,0.05)]",
                  // Hovering lifts an ordinary row to plain white; a tinted one
                  // keeps its tint, otherwise the marker vanishes under the
                  // pointer exactly when the user goes looking for it.
                  // In dark mode the row also lightens, and gets a deeper shadow plus a faint top highlight.
                  onRowClick && !selected && "hover:bg-white dark:hover:bg-slate-100",
                  selected && "bg-bridge-accent-tint",
                  // Checked rows tint too, so a selection spanning several pages is
                  // still visible after scrolling back to it.
                  !selected && checked && "bg-bridge-accent-wash",
                )}
              >
                {allColumns.map((c, index) => {
                  const isSelectionColumn = c.id === SELECTION_COLUMN_ID;
                  const pinned = isStickyColumn(index);
                  const rowBg = selected ? "bg-bridge-accent-tint" : checked ? "bg-bridge-accent-wash" : "bg-white";
                  const centered = isCentered(index);
                  // Cells often render block-level content (flex rows, icons),
                  // which text-center can't move — so centre it explicitly.
                  const content = centered ? (
                    <div className="flex w-full items-center justify-center text-center break-words">
                      {c.cell(row)}
                    </div>
                  ) : (
                    c.cell(row)
                  );
                  return (
                    <td
                      key={c.id}
                      // Ticking a checkbox must not also open the row's detail drawer.
                      onClick={isSelectionColumn ? (e) => e.stopPropagation() : undefined}
                      className={cn(
                        "px-5 text-bridge-fg align-middle border-b border-bridge-border/90",
                        cellPad,
                        // Pinned columns mirror the row's own background so they look
                        // identical, and being opaque nothing shows behind them while
                        // scrolling. z-[5] keeps them above cells sliding under them but
                        // below a hovered row (z-10), so that row's lift shadow still
                        // falls over the frozen block.
                        pinned && `sticky z-[5] transition-colors duration-150 ${rowBg}`,
                        // Mirrors the row's own hover rule above, so the pinned
                        // column never disagrees with the rest of its row.
                        pinned && onRowClick && !selected && "group-hover:bg-white dark:group-hover:bg-slate-100",
                        isSelectionColumn && "pl-8 pr-0",
                        isStickyEdge(index) && scrolledX && STICKY_COLUMN_EDGE,
                        centered ? "text-center tabular-nums" : c.align === "right" ? "text-right tabular-nums" : "text-left",
                        c.width,
                        c.className,
                      )}
                      style={{ width: resolvedColumnWidth(c), left: pinned ? stickyOffset(index) : undefined }}
                    >
                      {c.cellTooltip ? (
                        <Tooltip content={c.cellTooltip(row)} side="bottom-right" variant="muted" hoverOnly>
                          {content}
                        </Tooltip>
                      ) : (
                        content
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
